import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { DashboardService } from '../../../core/services/dashboard.service';
import { DashboardStatistics, StatItem } from '../../../core/models/dashboard.model';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-ui-stats',
  standalone: true,
  imports: [CommonModule, LoadingSpinnerComponent],
  templateUrl: './ui-stats.component.html',
  styleUrl: './ui-stats.component.scss',
})
export class UiStatsComponent implements OnInit, OnDestroy {
  public statsHome: StatItem[] = [];
  public loading = false;
  public error = false;

  private destroy$ = new Subject<void>();

  constructor(
    private dashboardService: DashboardService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadStatistics();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public loadStatistics(): void {
    // Verificar se o usuário está autenticado
    if (!this.authService.isAuthenticated()) {
      console.warn('Usuário não autenticado. Não é possível carregar estatísticas.');
      this.error = true;
      return;
    }

    this.loading = true;
    this.error = false;

    this.dashboardService.getStatistics()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.statsHome = this.mapStatisticsToStatsHome(response.data);
            this.loading = false;
          } else {
            console.warn('Resposta da API sem dados válidos:', response);
            this.error = true;
            this.loading = false;
          }
        },
        error: (error) => {
          console.error('Erro ao carregar estatísticas:', error);
          this.error = true;
          this.loading = false;
        }
      });
  }

  private mapStatisticsToStatsHome(data: DashboardStatistics): StatItem[] {
    const safeValue = (value: number | undefined): number => {
      return typeof value === 'number' ? value : 0;
    };

    const administradores = safeValue(data.administradores_banco_dados);
    const informadores = safeValue(data.informadores_banco_dados);
    const hasPreInformadoresField = data.pre_informadores_banco_dados !== undefined
      && data.pre_informadores_banco_dados !== null;

    // Compatível com API antiga (DEV sem deploy): deriva pré-informadores do total
    const preInformadores = hasPreInformadoresField
      ? safeValue(data.pre_informadores_banco_dados)
      : Math.max(0, safeValue(data.total_usuarios) - administradores - informadores);

    const totalUsuarios = hasPreInformadoresField
      ? safeValue(data.total_usuarios)
      : administradores + informadores;

    return [
      {
        title: 'Usuários Administradores',
        value: administradores,
        icon: 'bi bi-shield-check'
      },
      {
        title: 'Usuários Informadores',
        value: informadores,
        icon: 'bi bi-people'
      },
      {
        title: 'Pré-Informadores',
        value: preInformadores,
        icon: 'bi bi-person-plus'
      },
      {
        title: 'Total de usuários',
        value: totalUsuarios,
        icon: 'bi bi-person-check'
      },
      {
        title: 'Usuários ativos',
        value: safeValue(data.usuarios_ativos),
        icon: 'bi bi-person-check-fill'
      },
      {
        title: 'Usuários Bloqueados',
        value: hasPreInformadoresField
          ? safeValue(data.usuarios_blacklist)
          : 0,
        icon: 'bi bi-person-x'
      },
      {
        title: 'Circunscrições Eclesiásticas',
        value: safeValue(data.circunscricoes_eclesiasticas),
        icon: 'bi bi-building'
      },
      {
        title: 'Processos a iniciar',
        value: safeValue(data.provisoes_a_iniciar),
        icon: 'bi bi-clipboard'
      },
      {
        title: 'Processos em andamento',
        value: safeValue(data.provisoes_em_andamento),
        icon: 'bi bi-file-earmark-person'
      },
      {
        title: 'Processos concluídos',
        value: safeValue(data.provisoes_concluidas),
        icon: 'bi bi-journal'
      },
      {
        title: 'Processos fechados',
        value: safeValue(data.provisoes_fechadas),
        icon: 'bi bi-file-earmark-lock'
      },
      {
        title: 'Total de processos',
        value: safeValue(data.total_processos),
        icon: 'bi bi-folder'
      } 
    ];
  }
}
